import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { SnackBarOptions } from "@obg/mat.ui.core";
import { CAMPAIGN_MESSAGE_CODES, CAMPAIGN_MESSAGE_CONTEXT, CampaignConfigService, CommonState } from "@obg/ngx.common";
import { AbstractTranslatableSnackBarService, Message, MessageService, MessageType } from "@obg/ngx.core";
import { TranslationService } from "@obg/ngx.core.content";
import { RouteAction } from "@obg/ngx.router";
import { Observable, Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

@Injectable({
	providedIn: "root",
})
export class CampaignService {
	private campaignCodeParamName: string;
	private optInParamName: string;

	constructor(
		private abstractTranslatableSnackBarService: AbstractTranslatableSnackBarService,
		private config: CampaignConfigService,
		private messageService: MessageService,
		private store: Store<CommonState>,
		private translationService: TranslationService
	) {
		this.campaignCodeParamName = this.config.getCampaignCodeParamName();
		this.optInParamName = this.config.getOptInParamName();
	}

	successfulJoin$(): Observable<Message[]> {
		return this.messageService
			.consume$({
				messageCode: CAMPAIGN_MESSAGE_CODES.joinCampaign.success,
				context: CAMPAIGN_MESSAGE_CONTEXT.joinCampaignSuccess,
			})
			.pipe(
				tap(() => {
					this.showSnackBar({
						message: `campaign.${CAMPAIGN_MESSAGE_CODES.joinCampaign.success}`,
						action: "general.close",
						type: MessageType.Success,
						duration: 6000,
					});
				})
			);
	}

	successfulOptIn$(): Observable<Message[]> {
		return this.messageService
			.consume$({
				messageCode: CAMPAIGN_MESSAGE_CODES.optInCampaign.success,
				context: CAMPAIGN_MESSAGE_CONTEXT.optInCampaignSuccess,
			})
			.pipe(
				tap(() => {
					this.showSnackBar({
						message: `campaign.optin.${CAMPAIGN_MESSAGE_CODES.optInCampaign.success}`,
						action: "general.close",
						type: MessageType.Success,
						duration: 6000,
					});
				})
			);
	}

	failedJoin$(): Observable<Message[]> {
		return this.messageService
			.consume$({
				context: CAMPAIGN_MESSAGE_CONTEXT.joinCampaignError,
			})
			.pipe(
				filter((messages) => !!messages),
				tap((messages) => {
					const errorKey = `campaign.${messages[0].messageCode.toLowerCase()}`;
					const translatedErrorMessage = this.translationService.getByKey(errorKey);

					this.showSnackBar({
						message: errorKey === translatedErrorMessage ? "campaign.e_unhandled" : errorKey,
						action: "general.close",
						type: MessageType.Warn,
						duration: 6000,
					});
				})
			);
	}

	failedOptIn$(): Observable<Message[]> {
		return this.messageService
			.consume$({
				context: CAMPAIGN_MESSAGE_CONTEXT.optInCampaignError,
			})
			.pipe(
				filter((messages) => !!messages),
				tap((messages) => {
					const errorKey = `campaign.optin.${messages[0].messageCode.toLowerCase()}`;
					const translatedErrorMessage = this.translationService.getByKey(errorKey);

					this.showSnackBar({
						message: errorKey === translatedErrorMessage ? "campaign.e_unhandled" : errorKey,
						action: "general.close",
						type: MessageType.Warn,
						duration: 6000,
					});
				})
			);
	}

	private showSnackBar(options: SnackBarOptions): Subscription {
		return this.abstractTranslatableSnackBarService
			.openSnackBar$({ ...options })
			.pipe(
				switchMap((snackBarRef) =>
					snackBarRef.afterDismissed().pipe(
						take(1),
						tap(() => {
							this.store.dispatch(
								RouteAction.updateQueryParams({
									[this.optInParamName]: null,
									[this.campaignCodeParamName]: null,
								})
							);
						})
					)
				)
			)
			.subscribe();
	}
}
